/**
 * W-8ECI Form Models
 * Following the same clean architecture pattern as W8BEN models
 */
import { determineStateFieldType } from "../utils/formUtils";

import { parseAddressComponents as utilParseAddressComponents } from "../utils/formUtils";

// W8ECI Response Model - Fields that come from API endpoint
export class W8ECIResponseModel {
  // Basic fields typically from API
  "UniqueIdentifier": string = "";
  "Beneficial Owner name": string = "";
  "Permanent residence address": string = "";
  "Country_of_incorporation_organization": string = "";
  "Permanent city address": string = "";
  "Permanent address country": string = "";
  "U.S. taxpayer identification number": string = "";
  "Foreign tax identifying number": string = "";
  "Date of birth": string = "";
  "Form_name": string = "";
  "Form_revision": string = "";

  // ECI specific fields
  "ECI-11_Effectively_connected_income_details": string = "";
  "ECI-12_Securities_dealer": string = "";
  "ECI-Business_address_city_state_zip": string = "";
  "ECI-Business_address_street": string = "";
  "ECI-Business_address_line_2": string = "";
  "ECI-Business_address_line_3": string = "";
  "ECI-Central_bank_of_issue": string = "";
  "ECI-Complex_trust": string = "";
  "ECI-Corporation": string = "";
  "ECI-Disregarded_entity_name": string = "";
  "ECI-EIN": string = "";
  "ECI-Estate": string = "";
  "ECI-Government_controlled_entity": string = "";
  "ECI-Government_integral": string = "";
  "ECI-Grantor_trust": string = "";
  "ECI-Individual": string = "";
  "ECI-International_organization": string = "";
  "ECI-Partnership": string = "";
  "ECI-Private_foundation": string = "";
  "ECI-Reference_number": string = "";
  "ECI-Simple_trust": string = "";
  "ECI-Tax_exempt_organization": string = "";
  "ECI-Us_tin_SSN/ITIN": string = "";
  "FTIN_not_legally_required": string = "";

  // Signature fields
  "Signing_capacity": string = "";
  "Print_name": string = "";
  "Signature": string = "";
  "Signature_date": string = "";
  "signature_date": string = ""; // Form field variant (lowercase)
  "Signature_unreadable": boolean = false;
  "isSigned": string = "";
  "isModified": string = "no";
  "FormName": string = "W-8ECI";

  // Securities dealer certification field
  "Check here to certify that you are a dealer in securities": string = "";

  // Dynamic fields support
  [key: string]: any;

  constructor(data?: any) {
    if (data) {
      Object.assign(this, data);
    }
  }
}

// W8ECI Form Model - Complete form fields (more comprehensive)
export class W8ECIFormModel {
  // Basic identification fields
  "UniqueIdentifier": string = "";
  "Beneficial Owner name": string = "";
  "Form_name": string = "W-8ECI";
  "Form_revision": string = "";

  // Name fields (matching form field names)
  "First name and middle name": string = "";
  "Last name": string = "";
  // 'Name': string = '';

  // Form-specific field names for permanent address (actually used in form)
  "Permanent residence address": string = "";
  "Permanent_address_line_2": string = "";
  "Permanent_address_line_3": string = "";
  "Permanent_address_city": string = "";
  "Permanent_address_state_USCA": string = "";
  "Permanent_address_state_NonUS": string = "";
  "Permanent_address_zip": string = "";
  "Permanent address country": string = "";
  "Permanent_address_other": string = "";
  "Permanent_address_is_care_of": string = "no";
  "Permanent_address_is_po_box": string = "no";
  "Permanent_address_is_rural_route": string = "no";

  // Business address fields (used in form)
  "ECI-Business_address_street": string = "";
  "ECI-Business_address_line_2": string = "";
  "ECI-Business_address_line_3": string = "";
  "ECI-Business_address_city_state_zip": string = "";

  // Form-specific field names for business address (actually used in form)
  "Business_address_city": string = "";
  "Business_address_state_USCA": string = "";
  "Business_address_state_NonUS": string = "";
  "Business_address_zip": string = "";
  "Business_address_country": string = "";
  "Business_address_other": string = "";
  "Business_address_is_care_of": string = "no";
  "Business_address_is_po_box": string = "no";
  "Business_address_is_rural_route": string = "no";

  // Additional fields from form
  "Country_of_incorporation_organization": string = "";
  "Other": string = "";

  // Tax identification
  "U.S. taxpayer identification number": string = "";
  "Foreign tax identifying number": string = "";
  "ECI-Us_tin_SSN/ITIN": string = "";
  "ECI-EIN": string = "";
  "FTIN_not_legally_required": string = "";
  "Date of birth": string = "";

  // Entity type checkboxes
  "ECI-Individual": string = "";
  "ECI-Corporation": string = "";
  "ECI-Partnership": string = "";
  "ECI-Simple_trust": string = "";
  "ECI-Grantor_trust": string = "";
  "ECI-Complex_trust": string = "";
  "ECI-Estate": string = "";
  "ECI-Government_integral": string = "";
  "ECI-Government_controlled_entity": string = "";
  "ECI-International_organization": string = "";
  "ECI-Central_bank_of_issue": string = "";
  "ECI-Tax_exempt_organization": string = "";
  "ECI-Private_foundation": string = "";
  "ECI-Disregarded_entity_name": string = "";

  // ECI specific fields
  "ECI-11_Effectively_connected_income_details": string = "";
  "ECI-12_Securities_dealer": string = "";
  "ECI-Reference_number": string = "";

  // Signature and certification
  "Signing_capacity": string = "";
  "Print_name": string = "";
  "Signature": string = "";
  "Signature_date": string = "";

  "isSigned": string = "no";
  "isModified": string = "no";
  "FormName": string = "W-8ECI";

  // Additional signature/certification field from form
  "Check here to certify that you are a dealer in securities": string = "";

  // Mapping control field - prevents re-mapping when already done
  isMapDone: boolean = false;

  // Dynamic fields support
  [key: string]: any;

  constructor(data?: any) {
    if (data) {
      Object.assign(this, data);
    }
  }

  /**
   * Validate the form data
   */
  validate(): ValidationResult {
    const errors: string[] = [];
    const fieldErrors: Record<string, string> = {};

    // Required field validations - Updated per user requirements
    // Permanent address, Date of birth, and Country of citizenship removed as mandatory per user request
    const requiredFields = ["UniqueIdentifier", "Beneficial Owner name"];

    requiredFields.forEach((field) => {
      const value = (this as any)[field];
      if (!value || (typeof value === "string" && value.trim() === "")) {
        const fieldName = field.replace("ECI-", "").replace(/_/g, " ");
        const error = `${fieldName} is required`;
        errors.push(error);
        fieldErrors[field] = error;
      }
    });

    // Certification validation removed per user request

    return {
      isValid: errors.length === 0,
      errors,
      fieldErrors,
    };
  }

  /**
   * Check if a date string is valid
   */
  private isValidDate(dateString: string): boolean {
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date.getTime());
  }
}

export class ValidationResult {
  isValid: boolean = false;
  errors: string[] = [];
  fieldErrors: Record<string, string> = {};
}

/**
 * W8ECI Field Mapper - Maps response model fields to form model fields
 */
export class W8ECIFieldMapper {
  /**
   * Map response model to form model
   */
  static mapResponseToForm(responseModel: W8ECIResponseModel): W8ECIFormModel {
    const formModel = new W8ECIFormModel();

    // Check if mapping is already done to prevent re-mapping
    if (formModel.isMapDone) {
      console.log("W8ECI mapping already done, skipping...");
      return formModel;
    }

    // Direct field mappings (fields that exist in both models)
    const directMappings = [
      "UniqueIdentifier",
      "Beneficial Owner name",
      "Country_of_incorporation_organization",
      "U.S. taxpayer identification number",
      "Foreign tax identifying number",
      "Date of birth",
      "Form_name",
      "Form_revision",
      "ECI-11_Effectively_connected_income_details",
      "ECI-12_Securities_dealer",
      "ECI-Business_address_city_state_zip",
      "ECI-Business_address_street",
      "ECI-Business_address_line_2",
      "ECI-Business_address_line_3",
      "ECI-Central_bank_of_issue",
      "ECI-Complex_trust",
      "ECI-Corporation",
      "ECI-Disregarded_entity_name",
      "ECI-EIN",
      "ECI-Estate",
      "ECI-Government_controlled_entity",
      "ECI-Government_integral",
      "ECI-Grantor_trust",
      "ECI-Individual",
      "ECI-International_organization",
      "ECI-Partnership",
      "ECI-Private_foundation",
      "ECI-Reference_number",
      "ECI-Simple_trust",
      "ECI-Tax_exempt_organization",
      "ECI-Us_tin_SSN/ITIN",
      "FTIN_not_legally_required",
      "Signing_capacity",
      "Print_name",
      "Signature_date",
      "isSigned",
      "isModified",
      "FormName",
    ];

    directMappings.forEach((field) => {
      if (responseModel[field] !== undefined) {
        formModel[field] = responseModel[field];
      }
    });

    // Special mappings
    this.mapSpecialFields(responseModel, formModel);

    // Handle dynamic fields from response
    Object.keys(responseModel).forEach((key) => {
      if (!directMappings.includes(key) && responseModel[key] !== undefined) {
        formModel[key] = responseModel[key];
      }
    });

    // Set mapping done flag to prevent re-mapping
    formModel.isMapDone = true;
    console.log("W8ECI field mapping completed");

    return formModel;
  }

  /**
   * Handle special field mappings and transformations
   */
  private static mapSpecialFields(
    responseModel: W8ECIResponseModel,
    formModel: W8ECIFormModel
  ): void {
    // Map name fields - Split beneficial owner name if it exists
    if (responseModel["Beneficial Owner name"]) {
      const fullName = responseModel["Beneficial Owner name"];
      const nameParts = fullName.split(" ");
      if (nameParts.length >= 2) {
        formModel["First name and middle name"] = nameParts.slice(0, -1).join(" ");
        formModel["Last name"] = nameParts[nameParts.length - 1];
      } else {
        formModel["First name and middle name"] = fullName;
        formModel["Last name"] = "";
      }
      formModel["Beneficial Owner name"] = fullName;
    }

    // Map permanent address fields
    if (responseModel["Permanent residence address"]) {
      formModel["Permanent residence address"] = responseModel["Permanent residence address"];
    }

    if (responseModel["Permanent city address"]) {
      const addressComponents = this.parseAddressComponents(
        responseModel["Permanent city address"]
      );

      // Map to form field names (underscore versions used in form)
      formModel["Permanent_address_city"] = addressComponents.city;
      const stateFieldType = determineStateFieldType(
        responseModel["Permanent address country"],
        addressComponents.state
      );

      if (stateFieldType === "USCA") {
        formModel["Permanent address state USCA"] = addressComponents.state;
        formModel["Permanent address state NonUS"] = "";
      } else {
        formModel["Permanent address state NonUS"] = addressComponents.state;
        formModel["Permanent address state USCA"] = "";
      }
      formModel["Permanent_address_zip"] = addressComponents.postalCode;
    }

    if (responseModel["Permanent address country"]) {
      formModel["Permanent address country"] = responseModel["Permanent address country"];
    }

    // Map business address fields
    if (responseModel["ECI-Business_address_street"]) {
      formModel["ECI-Business_address_street"] = responseModel["ECI-Business_address_street"];
    }

    if (responseModel["ECI-Business_address_city_state_zip"]) {
      const addressComponents = this.parseAddressComponents(
        responseModel["ECI-Business_address_city_state_zip"]
      );

      // Map to form field names (underscore versions used in form)
      formModel["Business_address_city"] = addressComponents.city;
      const stateFieldType = determineStateFieldType(
        "Business_address_country",
        addressComponents.state
      );

      if (stateFieldType === "USCA") {
        formModel["Business address state USCA"] = addressComponents.state;
        formModel["Business address state NonUS"] = "";
      } else {
        formModel["Business address state NonUS"] = addressComponents.state;
        formModel["Business address state USCA"] = "";
      }
      formModel["Business_address_state_NonUS"] = addressComponents.state;
      formModel["Business_address_zip"] = addressComponents.postalCode;
    }

    // Map signature fields
    if (responseModel["Signature"]) {
      formModel["Signature"] = responseModel["Signature"];
    }

    if (responseModel["Signing_capacity"]) {
      formModel["Signing_capacity"] = responseModel["Signing_capacity"];
    }

    // Map securities dealer checkbox
    if (
      responseModel["ECI-12_Securities_dealer"] === "selected" ||
      responseModel["ECI-12_Securities_dealer"] === "true"
    ) {
      formModel["ECI-12_Securities_dealer"] = "selected";
    }

    // Auto-set isSigned based on Signature field
    if (responseModel["Signature"] && responseModel["Signature"].toString().trim()) {
      formModel["isSigned"] = "yes";
    } else {
      formModel["isSigned"] = "no";
    }

    if (responseModel["isModified"]) {
      formModel["isModified"] = responseModel["isModified"];
    }

    // Map additional response fields to form fields
    Object.keys(responseModel).forEach((responseField) => {
      const value = responseModel[responseField];
      if (value !== undefined && value !== null && value !== "") {
        // Direct assignment for fields that exist in form model
        if ((formModel as any)[responseField] !== undefined) {
          (formModel as any)[responseField] = value;
        }

        // Handle API fields with underscores/dashes - map to form fields with spaces
        const formFieldName = responseField.replace(/_/g, " ").replace(/-/g, " ");
        if (formFieldName !== responseField && (formModel as any)[formFieldName] !== undefined) {
          console.log(
            `W8ECI mapping API field "${responseField}" to form field "${formFieldName}"`
          );
          (formModel as any)[formFieldName] = value;
        }
      }
    });
  }

  /**
   * Parse address components from a combined address string
   */
  private static parseAddressComponents(address: string): {
    city: string;
    state: string;
    postalCode: string;
  } {
    // Use the utility function which has better postal code extraction logic
    return utilParseAddressComponents(address);
  }
}
